import { hydrate } from '@tanstack/react-query';
import type { DehydratedState } from '@tanstack/react-query';
import { StrictMode, startTransition } from 'react';
import type { ReactNode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { matchRoutes } from 'react-router';
import type { RouteObject } from 'react-router';
import { createQueryClient } from '@/lib/api/query-client';
import { HEAD_END, HEAD_START, STATE_SCRIPT_ID } from '@/prerender/markers';
import { App } from './App';
import { routeObjects } from './route-config';

/** The query cache the pre-render left in the page (`null` when there is none or it is unreadable). */
export function readDehydratedState(root: Document = document): DehydratedState | null {
  const text = root.getElementById(STATE_SCRIPT_ID)?.textContent;
  if (!text) return null;
  try {
    const state = JSON.parse(text) as Partial<DehydratedState>;
    return Array.isArray(state.queries) && Array.isArray(state.mutations)
      ? (state as DehydratedState)
      : null;
  } catch {
    return null;
  }
}

/**
 * Remove the head tags the pre-render put in (everything between its two marker comments). Used when
 * the page is rendered afresh instead of hydrated: React would otherwise add its own title, canonical
 * and Open Graph tags next to these, and the page would carry two of each.
 */
export function discardPrerenderedHead(head: HTMLHeadElement = document.head): void {
  const nodes = [...head.childNodes];
  const isMarker = (node: ChildNode, data: string): boolean =>
    node.nodeType === 8 && node.nodeValue === data;
  const start = nodes.findIndex((node) => isMarker(node, HEAD_START));
  const end = nodes.findIndex((node) => isMarker(node, HEAD_END));
  if (start === -1 || end < start) return;
  for (const node of nodes.slice(start, end + 1)) node.remove();
}

/**
 * Lazy route modules that match the address, loaded and merged into the route objects *before* the
 * router is created. Otherwise the router would start with the lazy routes unresolved and render
 * its `HydrateFallback` spinner instead of the page — which would not match the pre-rendered markup.
 * (This is what React Router's server-rendering guide prescribes for lazy routes.)
 */
export async function loadMatchedRoutes(
  routes: RouteObject[],
  location: { pathname: string; search: string; hash: string },
): Promise<void> {
  const lazyMatches = (matchRoutes(routes, location) ?? []).filter(
    ({ route }) => typeof route.lazy === 'function',
  );
  await Promise.all(
    lazyMatches.map(async ({ route }) => {
      if (typeof route.lazy !== 'function') return;
      const loaded = await route.lazy();
      Object.assign(route, { ...loaded, lazy: undefined });
    }),
  );
}

/**
 * Hydrate `container` at transition priority. React then works in slices of a few milliseconds and yields to
 * the browser between them (a tap or a scroll gets in at once), where the default priority hydrates the whole
 * page in one task that blocks the main thread for hundreds of milliseconds on a phone — the page's Total
 * Blocking Time. Events on markup that is not hydrated yet are replayed as soon as it is.
 */
function hydrateInSlices(container: HTMLElement, app: ReactNode): Root {
  const started: Root[] = [];
  startTransition(() => {
    started.push(hydrateRoot(container, app));
  });
  const [root] = started;
  if (!root) throw new Error('hydrateRoot did not start');
  return root;
}

/**
 * Start the app. The server-rendered page (when there is one) is *hydrated*: React adopts the
 * markup instead of rebuilding it, so the first client render has to equal the server HTML —
 *   · the lazy route modules of the address are loaded (see `loadMatchedRoutes`),
 *   · the query cache is filled from the page, so nothing flashes a skeleton, and counted as fresh
 *     (the snapshot is what the visitor is looking at; normal stale times apply from now on),
 *   · everything else that differs between server and browser (quality tier, motion, the signed-in
 *     user, the stored consent choice …) only changes in effects, after hydration.
 * With no usable server markup the app renders from scratch, as a single-page app.
 * `hydrating` is the entry's answer to "is the markup in `container` the pre-rendered page of this very
 * address?" (`canHydrate`, `hydratable.ts`): it asked already, to decide when to start.
 * Resolves with the React root (tests unmount it; the page never needs to).
 */
export async function bootClient(container: HTMLElement, hydrating: boolean): Promise<Root> {
  const { pathname, search, hash } = window.location;
  const prerendered = container.hasChildNodes();

  const queryClient = createQueryClient();
  const state = prerendered ? readDehydratedState() : null;
  if (state) {
    const now = Date.now();
    for (const query of state.queries) query.state.dataUpdatedAt = now;
    hydrate(queryClient, state);
  }

  const ready: Promise<unknown>[] = [];
  // Without server markup the router's own fallback covers the wait; with it, the visitor is looking
  // at the page already and must not see it replaced by a spinner.
  if (prerendered) {
    ready.push(loadMatchedRoutes(routeObjects, { pathname, search, hash }).catch(() => undefined));
  }
  await Promise.all(ready);
  if (prerendered && !hydrating) discardPrerenderedHead();

  const app = (
    <StrictMode>
      <App queryClient={queryClient} />
    </StrictMode>
  );
  if (hydrating) return hydrateInSlices(container, app);
  const root = createRoot(container);
  root.render(app);
  return root;
}
